import * as React from 'react';
import { MessageBar, MessageBarType, Stack } from '@fluentui/react';
import { useHistory } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { CmicContext } from '../../context/CmicContext';
import { ACTION_ROLE, CMIC_KEYS, CMIC_TREE_NODE_TYPES } from '../../shared/Constants';
import * as APP_CONSTANTS from '../../shared/Constants';
import ActionRoutes from '../tasks/ActionRoutes';
import { ActionContext } from '../../context/ActionContext';
import { SaveAttachments } from '../tasks/SaveAttachment';
import { customStyles } from '../../shared/styles';
import { handleProjectDataFiller } from '../../utility/UtilityFunctions';
import SettingsMenu from '../settings/SettingsMenu';
import {
	handleImagePathForDifferentEnv,
	handleLoginUserViaAuth2,
	isInternetAvailable,
} from '../../shared/UtilityFunctions';
import { analytics } from '../../utility/Analytics';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { segmentDefaultTrackCall, segmentDefaultPageCall } from '../../utility/segment/segmentUtility';
import axios from 'axios';

//Styles
const stackTokens = { childrenGap: 5 };

const headerStackStyle = {
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
	alignItems: 'center',
	padding: '0.1rem 0.3rem 0 0.2rem',
};

const loaderStyles = {
	alignItems: 'center',
	display: 'flex',
	justifyContent: 'center',
	flex: 1,
	height: '100%',
};

const Welcome = () => {
	const { useContext, useCallback, useState, useRef } = React;
	const {
		selectedProject,
		currentOfficeTheme,
		userInfo,
		showToast,
		fetchProjects,
		setSelectedProject,
		resetCMiCData,
		setShowToast,
	} = useContext(CmicContext);

	const {
		resetActionData,
		setActionTypeData,
		activeNode: activeActionNode,
		selectedImagingDoc,
		setSelectedImagingDoc,
		fetchImagingDocsType,
	} = useContext(ActionContext);

	const [disableDropbox, setDisableDropbox] = useState(false);
	const [showDialogForAttachment, setShowDialogForAttachment] = useState(false);
	const [isDropDownFocused, setIsDropDownFocused] = useState(false);
	const [defaultProjList, setDefaultProjList] = useState([]);
	const [imagingSources, setImagingSources] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isDropdownLoading, setIsDropDownLoading] = useState(false);

	const navigate = useHistory();

	//styles

	const iconStyles = {
		color: currentOfficeTheme.bodyForegroundColor,
		fontSize: '25px',
		marginRight: '0.6rem',
	};

	React.useEffect(() => {
		if (!activeActionNode) return;
		handleDropBoxVisibility();
	}, [activeActionNode]);

	//Analytics call while the user lands on Imaging home page
	React.useEffect(async () => {
		if (userInfo.actionRole === ACTION_ROLE.ECM) {
			await fetchImagingDocsType({ searchOption: '', offset: 0 });
		}
		if (userInfo.actionRole === ACTION_ROLE.ECM && userInfo?.isCloudUser) {
			let obj = {};
			obj = {
				appName: APP_CONSTANTS.APP_NAME,
				timeStamp: new Date(),
				mode: 'Cloud',
				contactCode: userInfo?.info?.contactCode,
				page_name: 'CMiC Imaging home page',
			};
			analytics.page({ name: 'Imaging Source' }, obj);
		} else if (userInfo.actionRole === ACTION_ROLE.PROJECT && userInfo?.isCloudUser) {
			// Trigger a Segment PAGE call when the "PM Home page" loads
			let obj = {};
			obj = {
				appName: APP_CONSTANTS.APP_NAME,
				timeStamp: new Date(),
				mode: 'Cloud',
				contactCode: userInfo?.info?.contactCode,
				page_name: 'CMiC PM home page',
			};
			segmentDefaultPageCall('PM_HOME_PAGE', obj);
		}
	}, []);

	const handleDropBoxVisibility = isVisible => {
		if (userInfo.actionRole === ACTION_ROLE.PROJECT) {
			if (navigate?.location?.pathname === '/home/actions') return setDisableDropbox(false);
			setDisableDropbox(true);
		} else {
			if (activeActionNode === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT || !activeActionNode) {
				setDisableDropbox(false);
			} else setDisableDropbox(true);
		}
	};

	const parentStackStyles = {
		root: {
			height: '100%',
			width: '100%',
			backgroundColor: currentOfficeTheme.bodyBackgroundColor,
			position: 'relative',
		},
	};

	const centerStyle = {
		alignItems: 'center',
		justifyContent: 'center',
		display: 'flex',
		textAlign: 'ceter',
	};

	const DivLoader = React.useCallback(
		() => (
			<div style={loaderStyles}>
				<Spinner size={SpinnerSize.large} label="Signing Out..." />
			</div>
		),
		[],
	);

	const Toast = useCallback(() => {
		if (!showToast.isVisible) return null;
		return (
			<div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10, display: 'flex', flex: 1 }}>
				<MessageBar
					messageBarType={showToast.type || MessageBarType.error}
					isMultiline={false}
					truncated
					dismissButtonAriaLabel="Close"
					onDismiss={event => {
						setShowToast({
							isVisible: false,
							type: null,
							msg: '',
						});
						event.stopPropagation();
					}}
				>
					{showToast.msg}
				</MessageBar>
			</div>
		);
	}, [showToast]);

	if (!userInfo.serverURL || (!userInfo.password && !userInfo.accessToken)) {
		return (
			<Stack tokens={stackTokens} style={centerStyle} styles={parentStackStyles}>
				<Stack.Item>
					<div style={{ color: currentOfficeTheme.controlForegroundColor, fontSize: 20 }}>
						Please add your details in <strong>settings</strong>.
					</div>
				</Stack.Item>
				<Stack.Item>
					<img
						onClick={() => navigate.replace('/settings')}
						src={`${handleImagePathForDifferentEnv()}user.png`}
						style={{ height: '50px', width: '50px', background: 'transparent', cursor: 'pointer' }}
					/>
				</Stack.Item>
			</Stack>
		);
	}

	const handleProjValueChange = data => {
		if (!data || !data.value) return;

		// Trigger a Segment Track call when the user manually changes the project
		if (userInfo.actionRole === ACTION_ROLE.PROJECT && userInfo?.isCloudUser)
			createSegmentTrack(data, 'User Selection', selectedProject);

		const projList = handleProjectDataFiller({
			timeStamp: new Date(),
			...data,
		});

		resetCMiCData();
		resetActionData();
		setSelectedProject(data);
		setActionTypeData({
			nodeType: null,
			label: null,
			type: null,
		});
		setIsDropDownFocused(false);
		setDefaultProjList(projList);

		return projList;
	};

	const createSegmentTrack = (currentProject, actionType, prevProjet) => {
		const obj = {
			contactCode: userInfo?.info?.contactCode,
			timeStamp: new Date(),
			companyCode: currentProject?.PmpCompCode,
			companyName: currentProject?.PmpCompName,
			ProjectId: currentProject?.PmpVUuid,
			ProjectName: currentProject?.PmpProjName,
			previousProjectId: prevProjet?.PmpVUuid ? prevProjet?.PmpVUuid : '',
			previousProjectName: prevProjet?.PmpProjName ? prevProjet?.PmpProjName : '',
			actionType: actionType,
		};

		segmentDefaultTrackCall('PM_SELECT_PROJECT', obj);
	};

	const handleImgSourceChange = data => {
		if (!data || !data.value) return;
		setSelectedImagingDoc(prev => {
			//Analytics call while the user selects Imaging source from LOV.
			if (prev?.value !== data?.value && userInfo?.isCloudUser) {
				let obj = {};
				obj = {
					contactCode: userInfo?.info?.contactCode,
					timestamp: new Date(),
					imagingSourceVUuid: data?.ImgsVUuid,
					imagingSourceName: data?.ImgsSourceDesc,
					previousImagingSourceVUuid: prev?.ImgsVUuid,
					previousImagingSourceName: prev?.ImgsSourceDesc,
					actionType: 'User Selection',
				};
				analytics.track({ event: 'Imaging Source Selected' }, obj);
			}
			return data;
		});
		localStorage.setItem(CMIC_KEYS.SELCTED_IMAGING_DOC_ENCRYPTION_KEY, JSON.stringify(data));
	};

	const fetchNFilterConcatImagingSourceData = async ({ searchOption = '', offset = 0 }) => {
		return new Promise(async (resolve, reject) => {
			try {
				const encodedQuery = encodeURIComponent(` LIKE UPPER('%${searchOption}%')`);
				const URL = `${userInfo.serverURL}${APP_CONSTANTS.CMIC_FIELD_REST__NAMES.ImgSources
					}?onlyData=true&totalResults=false&limit=${APP_CONSTANTS.PAGE_LIMIT}&offset=${APP_CONSTANTS.PAGE_LIMIT * offset
					}&orderBy=ImgsSourceDesc,ImgsVUuid&q=ImgsSourceDesc${encodedQuery}`;

				const result = await axios.get(URL, {
					...handleLoginUserViaAuth2(userInfo),
				});

				const requiredData = [];

				(result.data.items || []).forEach(item => {
					// Removing inactive imaging sources
					if (item?.ImgsActiveFlag === 'N' || item?.ImgsAllSourcesFlag === 'Y') return;
					requiredData.push({
						value: item.ImgsVUuid,
						label: item.ImgsSourceDesc,
						...item,
					});
				});

				resolve({
					mainData: {
						items: requiredData,
						hasMore: result.data?.hasMore,
						limit: result.data?.limit,
						offset: result.data?.offset + 1,
						count: result.data?.count,
					},
				});
			} catch (error) {
				console.log('fetchNFilterConcatData() ERROR', error);

				resolve({
					mainData: {
						items: [],
						hasMore: null,
						limit: null,
						offset: null,
						count: null,
					},
				});
			}
		});
	};

	const fetchConcatsOnLoadMore = async val => {
		setIsDropDownLoading(true);
		if (userInfo.actionRole === ACTION_ROLE.ECM) {
			if (imagingSources?.hasMore) {
				const result = await fetchNFilterConcatImagingSourceData({
					searchOption: '',
					offset: imagingSources?.offset,
				});
				let newImagingSourceArray = [];
				setImagingSources(prev => {
					newImagingSourceArray = [...prev?.items, ...result?.mainData?.items];
					return { ...result?.mainData, items: newImagingSourceArray };
				});
				setIsDropDownLoading(false);
				return result?.mainData?.items;
			}
		}
		setIsDropDownLoading(false);
	};

	const promiseOptions = async val => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}

		if (userInfo.actionRole === ACTION_ROLE.ECM) {
			setIsDropDownLoading(true);
			const result = await fetchImagingDocsType({ searchOption: val, offset: 0 });
			setImagingSources(result?.mainData);
			setIsDropDownLoading(false);
			return result?.mainData?.items;
		} else {
			// if(timer.current) clearTimeout(timer.current);
			// Added delay to prevent API calling for every key pressed
			let result = null;
			if (val) {
				result = await fetchProjects({ search: val }, userInfo);
				return result;
			}

			const projList = handleProjectDataFiller();

			if (projList.length > 0) {
				setSelectedProject(projList[0]);
				setDefaultProjList(projList);

				// Trigger a Segment TRACK call when the project is auto-selected
				if (userInfo.actionRole === ACTION_ROLE.PROJECT && userInfo?.isCloudUser) {
					let action = 'Cached Selection';
					if (projList.length === 0 && userInfo.info?.defaultProjectName) {
						action = 'Default Selection';
					}
					createSegmentTrack(projList[0], action);
				}
				return projList;
			}
			return [];
		}
	};

	let mainUI = <ActionRoutes enableDropbox={handleDropBoxVisibility} />;

	if (!selectedProject.value && userInfo.actionRole === ACTION_ROLE.PROJECT && !disableDropbox) {
		mainUI = (
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					height: '100%',
					width: '100wh',
					background: 'transparent',
				}}
			>
				<strong style={{ fontSize: 18, color: currentOfficeTheme.controlForegroundColor }}>
					Please select any project.
				</strong>
			</div>
		);
	}

	const setCallBackForShowDialog = async () => {
		setShowDialogForAttachment(false);
	};

	const CaretDownIcon = () => {
		return <span className="material-symbols-outlined">arrow_drop_down</span>;
	};

	const DropdownIndicator = props => {
		return (
			<components.DropdownIndicator {...props}>
				<CaretDownIcon />
			</components.DropdownIndicator>
		);
	};

	const Placeholder = props => {
		return <components.Placeholder {...props} />;
	};

	if (isLoading) return <DivLoader />;

	return (
		<Stack styles={parentStackStyles} tokens={stackTokens}>
			<Toast />
			{/* We'll show/hide header from this stack */}
			{!disableDropbox ? (
				<Stack style={headerStackStyle}>
					{userInfo.actionRole === ACTION_ROLE.ECM && (
						<span style={iconStyles} className="material-symbols-outlined">
							home
						</span>
					)}
					<Stack.Item style={{ width: '100%', zIndex: 8 }}>
						<AsyncSelect
							value={
								userInfo.actionRole === ACTION_ROLE.ECM
									? selectedImagingDoc?.value
										? selectedImagingDoc
										: ''
									: isDropDownFocused
										? ''
										: selectedProject
							}
							isSearchable
							required
							aria-label="Projects"
							components={{ Placeholder, DropdownIndicator }}
							// cacheOptions
							defaultOptions={
								userInfo.actionRole === ACTION_ROLE.ECM
									? imagingSources?.items
									: defaultProjList.length === 0
										? true
										: defaultProjList
							}
							placeholder={
								userInfo.actionRole === ACTION_ROLE.ECM
									? 'Please select an Imaging Source'
									: 'Please select a Project'
							}
							loadOptions={promiseOptions}
							isLoading={isDropdownLoading}
							// options={imagingSources?.items}
							onMenuScrollToBottom={fetchConcatsOnLoadMore}
							onBlur={() => {
								setIsDropDownFocused(false);
								setImagingSources([]);
							}}
							onMenuOpen={() => {
								promiseOptions();
								setIsDropDownFocused(true);
							}}
							styles={customStyles(currentOfficeTheme)}
							onChange={
								userInfo.actionRole === ACTION_ROLE.ECM ? handleImgSourceChange : handleProjValueChange
							}
						/>
					</Stack.Item>
					<SettingsMenu isLoading={isLoading} setIsLoading={setIsLoading} />
				</Stack>
			) : null}
			{mainUI}
			{showDialogForAttachment ? (
				<SaveAttachments
					userInfo={userInfo}
					selectedProject={selectedProject}
					showDialogForAttachment={showDialogForAttachment}
					setCallBackForShowDialog={setCallBackForShowDialog}
					currentOfficeTheme={currentOfficeTheme}
				/>
			) : (
				<></>
			)}
		</Stack>
	);
};

export default Welcome;
